import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { MobileChatModeProvider, useMobileChatMode } from "@/hooks/useMobileChatMode";
import { Button } from "@/components/ui/button";
import { LogOut, ShieldOff } from "lucide-react";
import PostHogPageView from "@/components/PostHogPageView";
import TopNav from "@/components/TopNav";
import MobileHeader from "@/components/MobileHeader";
import MobileBottomNav from "@/components/MobileBottomNav";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import DataIngestionTicker from "@/components/DataIngestionTicker";
import Chat from "./pages/Chat";
import ComingSoon from "./pages/ComingSoon";
import Admin from "./pages/Admin";
import SuperAdmin from "./pages/SuperAdmin";
import DataConnectors from "./pages/DataConnectors";
import ExternalInterface from "./pages/ExternalInterface";
import DataGovernance from "./pages/DataGovernance";
import Observability from "./pages/Observability";
import DataGraph from "./pages/DataGraph";
import Deployment from "./pages/Deployment";
import InboundMcpServers from "./pages/InboundMcpServers";
import SyncingMonitor from "./pages/SyncingMonitor";
import PipelineDetail from "./pages/PipelineDetail";
import Auth from "./pages/Auth";
import SignOut from "./pages/SignOut";
import SharedDashboard from "./pages/SharedDashboard";
import Dashboards from "./pages/Dashboards";
import Queries from "./pages/Queries";
import Reports from "./pages/Reports";

import Home from "./pages/Home";
import MobileSettings from "./pages/MobileSettings";
import MobileResearchList from "./pages/MobileResearchList";
import NotFound from "./pages/NotFound";
import Workflows from "./pages/Workflows";
import AdvancedFeatures from "./pages/AdvancedFeatures";

const queryClient = new QueryClient();

/** Returns the subdomain if on *.databull.ai, otherwise null. */
function getDatabullSubdomain(): string | null {
  const { hostname } = window.location;
  if (hostname.endsWith(".databull.ai")) {
    return hostname.slice(0, hostname.length - ".databull.ai".length) || null;
  }
  return null;
}

/** True when running on the root databull.ai domain (no subdomain). */
function isRootDatabullDomain(): boolean {
  return window.location.hostname === "databull.ai";
}

/**
 * Read the shared .databull.ai cookie that records the urlslug subdomain the
 * current browser user last authenticated on. Returns null when absent.
 */
function getUrlslugCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)dfg_urlslug=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

const DisabledPage = () => {
  const { signOut } = useAuth();
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="rounded-full bg-muted p-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-10 w-10 text-muted-foreground"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
          />
        </svg>
      </div>
      <h1 className="text-2xl font-semibold">Account Pending</h1>
      <p className="max-w-md text-muted-foreground">The admin team will activate your account shortly.</p>
      <Button variant="outline" className="gap-2" onClick={() => signOut()}>
        <LogOut className="h-4 w-4" />
        Sign out
      </Button>
    </div>
  );
};

const ClientDisabledPage = () => (
  <div className="flex h-screen flex-col items-center justify-center gap-4 px-6 text-center">
    <div className="rounded-full bg-muted p-4">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-10 w-10 text-muted-foreground"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"
        />
      </svg>
    </div>
    <h1 className="text-2xl font-semibold">Organization Deactivated</h1>
    <p className="max-w-md text-muted-foreground">
      Your organization has been deactivated. Please contact your administrator for assistance.
    </p>
  </div>
);

const IpBlockedPage = () => {
  const { signOut } = useAuth();
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="rounded-full bg-muted p-4">
        <ShieldOff className="h-10 w-10 text-muted-foreground" />
      </div>
      <h1 className="text-2xl font-semibold">Access Denied</h1>
      <p className="max-w-md text-muted-foreground">
        Your IP address is not whitelisted. Please contact your administrator to request access
        or connect from an authorized network.
      </p>
      <Button variant="outline" className="gap-2" onClick={() => signOut()}>
        <LogOut className="h-4 w-4" />
        Sign out
      </Button>
    </div>
  );
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, profile, clientInfo, loading, ipBlocked, signOut, session } = useAuth();
  // Wait for clientInfo when the profile has a client_id — avoids false mismatch during load
  const waitingForClient = user && profile?.client_id && !clientInfo;
  if (loading || (user && !profile) || waitingForClient) return <div className="flex h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) {
    // If there's a shared urlslug cookie, the user is authenticated on their
    // subdomain — redirect there instead of showing the login page.
    const slug = getUrlslugCookie();
    const sub = getDatabullSubdomain();
    if (slug && (sub === "app" || isRootDatabullDomain())) {
      window.location.href = `https://${slug}.databull.ai${window.location.pathname}`;
      return null;
    }
    return <Navigate to="/auth" replace />;
  }
  if (ipBlocked) return <IpBlockedPage />;
  if (profile && !profile.super_admin) {
    if (!profile.enabled) return <DisabledPage />;
    if (clientInfo && !clientInfo.enabled) return <ClientDisabledPage />;

    const sub = getDatabullSubdomain();
    // On a client-specific subdomain only that client's users are allowed in
    if (sub && sub !== "app") {
      if (clientInfo?.urlslug !== sub) {
        signOut();
        return <Navigate to="/auth" replace />;
      }
    }
    // On app/root databull domain redirect client users to their own subdomain,
    // carrying the session so they don't have to log in again.
    if (clientInfo?.urlslug && (isRootDatabullDomain() || sub === "app")) {
      const hash = session
        ? `#access_token=${session.access_token}&refresh_token=${session.refresh_token}&token_type=bearer&expires_in=${session.expires_in}`
        : "";
      window.location.href = `https://${clientInfo.urlslug}.databull.ai${window.location.pathname}${hash}`;
      return null;
    }
  }
  return <>{children}</>;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, profile, clientInfo, loading, session } = useAuth();
  const waitingForClient = user && profile?.client_id && !clientInfo;
  if (loading || (user && !profile) || waitingForClient)
    return <div className="flex h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) {
    const slug = getUrlslugCookie();
    const sub = getDatabullSubdomain();
    if (slug && (sub === "app" || isRootDatabullDomain())) {
      window.location.href = `https://${slug}.databull.ai${window.location.pathname}`;
      return null;
    }
    return <Navigate to="/auth" replace />;
  }
  if (!profile?.admin && !profile?.super_admin) return <Navigate to="/" replace />;
  // Redirect client admins from app/root domain to their urlslug domain
  if (clientInfo?.urlslug && !profile.super_admin) {
    const sub = getDatabullSubdomain();
    if (isRootDatabullDomain() || sub === "app") {
      const hash = session
        ? `#access_token=${session.access_token}&refresh_token=${session.refresh_token}&token_type=bearer&expires_in=${session.expires_in}`
        : "";
      window.location.href = `https://${clientInfo.urlslug}.databull.ai${window.location.pathname}${hash}`;
      return null;
    }
  }
  return <>{children}</>;
};

const SuperAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, profile, loading } = useAuth();
  if (loading || (user && !profile))
    return <div className="flex h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (!profile?.super_admin) return <Navigate to="/" replace />;
  return <>{children}</>;
};

/** Blocks viewer-only users from accessing non-viewer pages (Research, Queries, AI Agents, etc.) */
const NonViewerRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, profile, clientInfo, loading, ipBlocked, signOut, session } = useAuth();
  const waitingForClient = user && profile?.client_id && !clientInfo;
  if (loading || (user && !profile) || waitingForClient) return <div className="flex h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) {
    const slug = getUrlslugCookie();
    const sub = getDatabullSubdomain();
    if (slug && (sub === "app" || isRootDatabullDomain())) {
      window.location.href = `https://${slug}.databull.ai${window.location.pathname}`;
      return null;
    }
    return <Navigate to="/auth" replace />;
  }
  if (ipBlocked) return <IpBlockedPage />;
  if (profile && !profile.super_admin) {
    if (!profile.enabled) return <DisabledPage />;
    if (clientInfo && !clientInfo.enabled) return <ClientDisabledPage />;

    const sub = getDatabullSubdomain();
    if (sub && sub !== "app") {
      if (clientInfo?.urlslug !== sub) {
        signOut();
        return <Navigate to="/auth" replace />;
      }
    }
    if (clientInfo?.urlslug && (isRootDatabullDomain() || sub === "app")) {
      const hash = session
        ? `#access_token=${session.access_token}&refresh_token=${session.refresh_token}&token_type=bearer&expires_in=${session.expires_in}`
        : "";
      window.location.href = `https://${clientInfo.urlslug}.databull.ai${window.location.pathname}${hash}`;
      return null;
    }
  }
  if (profile?.viewer && !profile?.admin && !profile?.super_admin) {
    return <Navigate to="/dashboards" replace />;
  }
  return <>{children}</>;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, profile, clientInfo, loading, session, signOut } = useAuth();

  const waitingForClient = user && profile?.client_id && !clientInfo;
  if (loading || (user && !profile) || waitingForClient) {
    return <div className="flex h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  }

  if (user) {
    if (profile && clientInfo?.urlslug) {
      const targetOrigin = `https://${clientInfo.urlslug}.databull.ai`;
      const sub = getDatabullSubdomain();
      const isDatabullDomain =
        window.location.hostname.endsWith(".databull.ai") ||
        window.location.hostname === "databull.ai";

      if (isDatabullDomain && window.location.origin !== targetOrigin) {
        // Only forward from a generic domain (app / root). If we're already on
        // a specific client subdomain the user doesn't belong here — sign them
        // out and show an access-denied error on the login page.
        if (!sub || sub === "app") {
          const hash = session
            ? `#access_token=${session.access_token}&refresh_token=${session.refresh_token}&token_type=bearer&expires_in=${session.expires_in}`
            : "";
          window.location.href = `${targetOrigin}${hash}`;
          return null;
        }
        // Wrong client on a urlslug domain
        signOut();
        return (
          <Navigate
            to="/auth"
            replace
            state={{ error: "You don't have access to this workspace. Please log in with an account that belongs to this organisation." }}
          />
        );
      }
    }
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const { active: mobileChatActive } = useMobileChatMode();

  return (
    <div className="flex h-screen flex-col">
      <ImpersonationBanner />
      {/* Desktop header – in flow, hidden on mobile */}
      <div className="hidden md:block shrink-0">
        <TopNav />
        <DataIngestionTicker />
      </div>
      {/* Mobile header – fixed, hidden on desktop; hidden when mobile chat is active */}
      {!mobileChatActive && (
        <div className="md:hidden">
          <MobileHeader />
        </div>
      )}
      {/* pt-14 pb-16 on mobile offset the fixed header (h-14) and bottom nav (h-16); removed when mobile chat active */}
      <div className={`flex flex-1 min-h-0 ${mobileChatActive ? "pt-0 pb-0" : "pt-14 pb-16"} md:pt-0 md:pb-0`}>{children}</div>
      {/* Mobile bottom nav – fixed, hidden on desktop; hidden when mobile chat is active */}
      {!mobileChatActive && (
        <div className="md:hidden">
          <MobileBottomNav />
        </div>
      )}
    </div>
  );
};

const SentryFallback = () => (
  <div className="flex h-screen flex-col items-center justify-center gap-4 px-6 text-center">
    <h1 className="text-2xl font-semibold">Something went wrong</h1>
    <p className="max-w-md text-muted-foreground">
      An unexpected error occurred. Please refresh the page to try again.
    </p>
    <Button variant="outline" onClick={() => window.location.reload()}>
      Refresh Page
    </Button>
  </div>
);

const App = () => (
  <Sentry.ErrorBoundary fallback={SentryFallback}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <PostHogPageView />
          <AuthProvider>
            <MobileChatModeProvider>
              <Routes>
              <Route
                path="/auth"
                element={
                  <PublicRoute>
                    <Auth />
                  </PublicRoute>
                }
              />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Navigate to="/research" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/home"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Home />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/research"
                element={
                  <NonViewerRoute>
                    <AppLayout>
                      <Chat />
                    </AppLayout>
                  </NonViewerRoute>
                }
              />

              <Route
                path="/dashboards"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Dashboards />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/reports"
                element={
                  <NonViewerRoute>
                    <AppLayout>
                      <Reports />
                    </AppLayout>
                  </NonViewerRoute>
                }
              />

              <Route
                path="/queries"
                element={
                  <NonViewerRoute>
                    <AppLayout>
                      <Queries />
                    </AppLayout>
                  </NonViewerRoute>
                }
              />

              <Route
                path="/aiagents"
                element={
                  <NonViewerRoute>
                    <AppLayout>
                      <Workflows />
                    </AppLayout>
                  </NonViewerRoute>
                }
              />
              <Route
                path="/advanced-features"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <AdvancedFeatures />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/data-connectors"
                element={
                  <AdminRoute>
                    <AppLayout>
                      <DataConnectors />
                    </AppLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/sync-monitor"
                element={
                  <AdminRoute>
                    <AppLayout>
                      <SyncingMonitor />
                    </AppLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/sync-monitor/:id"
                element={
                  <AdminRoute>
                    <AppLayout>
                      <PipelineDetail />
                    </AppLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/external-interface"
                element={
                  <AdminRoute>
                    <AppLayout>
                      <ExternalInterface />
                    </AppLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/data-governance"
                element={
                  <AdminRoute>
                    <AppLayout>
                      <DataGovernance />
                    </AppLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/inbound-mcp-servers"
                element={
                  <AdminRoute>
                    <AppLayout>
                      <InboundMcpServers />
                    </AppLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/observability"
                element={
                  <AdminRoute>
                    <AppLayout>
                      <Observability />
                    </AppLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/data-graph"
                element={
                  <AdminRoute>
                    <AppLayout>
                      <DataGraph />
                    </AppLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/deployment"
                element={
                  <AdminRoute>
                    <AppLayout>
                      <Deployment />
                    </AppLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/syncing-monitor"
                element={<Navigate to="/data-connectors?connector=syncing-monitor" replace />}
              />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AppLayout>
                      <Admin />
                    </AppLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/superadmin"
                element={
                  <SuperAdminRoute>
                    <AppLayout>
                      <SuperAdmin />
                    </AppLayout>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <MobileSettings />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/conversations"
                element={
                  <NonViewerRoute>
                    <AppLayout>
                      <MobileResearchList />
                    </AppLayout>
                  </NonViewerRoute>
                }
              />
              {/* Standalone sign-out page — no auth guards, visited from urlslug domain after logout */}
              <Route path="/signout" element={<SignOut />} />
              <Route path="/shared/:id" element={<SharedDashboard />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </MobileChatModeProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
